/**
 * Driver Registration Page - Module Inscription Ticket 2
 * Route: /driver/register
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Eye, EyeOff, UserPlus, AlertCircle, ArrowLeft, Car, Phone, Mail, User, Building2 } from 'lucide-react';
import config from 'config';

const API = `${config.API_URL}/api`;

export default function DriverRegisterPage({ embedded = false }) {
  const navigate = useNavigate();
  const [searchParams] = useState(() => new URLSearchParams(window.location.search));
  const inviteToken = searchParams.get('invite');

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [inviteInfo, setInviteInfo] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Ticket 1.1: Validate invitation token if present
  useEffect(() => {
    if (inviteToken) {
      fetch(`${API}/auth/driver/invite/validate?token=${inviteToken}`)
        .then(r => r.json())
        .then(data => {
          if (data.valid) {
            setInviteInfo(data);
            setFormData(prev => ({ ...prev, email: data.email || '' }));
          }
        })
        .catch(() => {});
    }
  }, [inviteToken]);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);
    try {
      const { confirmPassword, ...payload } = formData;
      // Ticket 1.1: If invitation token, attach it
      if (inviteToken) {
        payload.invite_token = inviteToken;
      }

      const res = await fetch(`${API}/auth/driver/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || "Erreur lors de l'inscription");
      }

      navigate('/driver/verify', { state: { email: data.email } });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={embedded ? "flex items-center justify-center px-4 py-8" : "min-h-screen bg-[#0B0B0B] flex items-center justify-center px-4 py-8"}>
      <div className="w-full max-w-md">
        {!embedded && (
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-[#C9A24D] hover:text-[#d4af5a] transition-colors mb-6" data-testid="back-home-link">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Retour</span>
          </Link>
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[#C9A24D]/10 border border-[#C9A24D]/30 rounded-lg flex items-center justify-center">
              <Car className="w-5 h-5 text-[#C9A24D]" />
            </div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Devenir Chauffeur</h1>
          </div>
          <p className="text-[#A0A0A0] text-sm">Rejoignez la flotte Y GO VTC Premium</p>
        </div>
        )}

        <Card className="bg-[#141414] border-[#2A2A2A]">
          <CardHeader className="pb-4">
            <CardTitle className="text-white text-lg">Inscription Chauffeur</CardTitle>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="flex items-start gap-2 p-3 mb-4 bg-red-500/10 border border-red-500/20 rounded-lg" data-testid="register-error">
                <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="driver-register-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="last_name" className="text-[#E5E5E5] text-xs">Nom</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                    <Input
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      required
                      placeholder="Dupont"
                      className="pl-9 bg-[#1A1A1A] border-[#333] text-white placeholder:text-[#555] h-10"
                      data-testid="driver-last-name-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="first_name" className="text-[#E5E5E5] text-xs">Prénom</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                    <Input
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      required
                      placeholder="Jean"
                      className="pl-9 bg-[#1A1A1A] border-[#333] text-white placeholder:text-[#555] h-10"
                      data-testid="driver-first-name-input"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-[#E5E5E5] text-xs">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="jean.dupont@email.com"
                    className="pl-9 bg-[#1A1A1A] border-[#333] text-white placeholder:text-[#555] h-10"
                    data-testid="driver-email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone" className="text-[#E5E5E5] text-xs">Téléphone</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="+33 6 12 34 56 78"
                    className="pl-9 bg-[#1A1A1A] border-[#333] text-white placeholder:text-[#555] h-10"
                    data-testid="driver-phone-input"
                  />
                </div>
              </div>

              {/* Ticket 1.1: Show fleet info if invited, no manual selection */}
              {inviteInfo && (
                <div className="flex items-center gap-2 p-3 bg-[#C9A24D]/10 border border-[#C9A24D]/20 rounded-lg">
                  <Building2 className="w-4 h-4 text-[#C9A24D] shrink-0" />
                  <div>
                    <p className="text-[#C9A24D] text-xs font-medium">Invitation flotte</p>
                    <p className="text-white text-sm">{inviteInfo.fleet_name}</p>
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="password" className="text-[#E5E5E5] text-xs">Mot de passe</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={8}
                    placeholder="Min. 8 caractères, 1 maj., 1 chiffre"
                    className="pr-10 bg-[#1A1A1A] border-[#333] text-white placeholder:text-[#555] h-10"
                    data-testid="driver-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666] hover:text-[#999]"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-[#E5E5E5] text-xs">Confirmer le mot de passe</Label>
                <div className="relative mt-1">
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    onPaste={(e) => e.preventDefault()}
                    onCopy={(e) => e.preventDefault()}
                    required
                    placeholder="Retapez votre mot de passe"
                    className="bg-[#1A1A1A] border-[#333] text-white placeholder:text-[#555] h-10"
                    data-testid="driver-confirm-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-[#C9A24D] hover:bg-[#d4af5a] text-black font-semibold"
                data-testid="driver-register-submit"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    S'inscrire
                  </>
                )}
              </Button>
            </form>

            <div className="mt-4 text-center">
              <p className="text-[#666] text-sm">
                Déjà inscrit ?{' '}
                <Link to="/login" className="text-[#C9A24D] hover:text-[#d4af5a] font-medium" data-testid="login-link">
                  Se connecter
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
